/**
 * ScreenBEditor — Moderator workspace for Screen B events.
 *
 * CRUD for screen_b_events: 4 quadrants, add/delete/edit/reorder.
 * Freeze/publish controls. Browse all observatory events to promote.
 *
 * SPEC: MODULES/M8_PUBLIC_SCREEN/SPEC_PUBLIC_SCREEN_SPRINT.md (Section 6)
 */

import { useEffect, useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

interface ScreenBEvent {
  id: string
  quadrant: string
  headline: string
  description: string
  sort_order: number
}

interface ObservatoryEvent {
  id: string
  event_type: string
  category: string | null
  country_code: string | null
  summary: string
  created_at: string
}

const QUADRANTS = ['military', 'diplomacy', 'political', 'economy_tech'] as const
const QUADRANT_LABELS: Record<string, string> = {
  military: 'Military',
  diplomacy: 'Diplomacy',
  political: 'Political',
  economy_tech: 'Economy & Tech',
}
const QUADRANT_COLORS: Record<string, string> = {
  military: 'text-red-400/70',
  diplomacy: 'text-blue-300/70',
  political: 'text-emerald-400/70',
  economy_tech: 'text-yellow-300/70',
}

const API_BASE = import.meta.env.VITE_API_URL ?? ''

async function getToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession()
  return data.session?.access_token ?? null
}

async function apiFetch(url: string, opts: RequestInit = {}): Promise<Record<string, unknown>> {
  const token = await getToken()
  const resp = await fetch(`${API_BASE}${url}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...((opts.headers as Record<string, string>) || {}),
    },
  })
  const json = await resp.json()
  if (!resp.ok) throw new Error((json as Record<string, string>).detail || 'Request failed')
  return json.data as Record<string, unknown>
}

export function ScreenBEditor({ simId }: { simId: string }) {
  const [events, setEvents] = useState<ScreenBEvent[]>([])
  const [frozen, setFrozen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showBrowser, setShowBrowser] = useState(true)
  const [showCustom, setShowCustom] = useState(false)
  const [customQuadrant, setCustomQuadrant] = useState<string>('military')
  const [customHeadline, setCustomHeadline] = useState('')
  const [customDescription, setCustomDescription] = useState('')
  const [obsEvents, setObsEvents] = useState<ObservatoryEvent[]>([])
  const [obsFilter, setObsFilter] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editHeadline, setEditHeadline] = useState('')
  const [editDescription, setEditDescription] = useState('')

  const loadEvents = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/sim/${simId}/screen-b/events?version=live`)
      setEvents((data.events as ScreenBEvent[]) || [])
    } catch { /* non-critical */ }
  }, [simId])

  const loadFrozen = useCallback(async () => {
    const { data } = await supabase.from('sim_runs').select('screen_b_frozen').eq('id', simId).single()
    if (data) setFrozen(data.screen_b_frozen ?? false)
  }, [simId])

  useEffect(() => {
    loadEvents()
    loadFrozen()
    loadObsEvents()
    const ch = supabase.channel(`sb-editor-${simId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'screen_b_events', filter: `sim_run_id=eq.${simId}` }, () => loadEvents())
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'sim_runs', filter: `id=eq.${simId}` }, () => loadFrozen())
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'observatory_events', filter: `sim_run_id=eq.${simId}` }, () => loadObsEvents())
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [simId, loadEvents, loadFrozen])

  const handleDelete = async (eventId: string) => {
    await apiFetch(`/api/sim/${simId}/screen-b/events/${eventId}`, { method: 'DELETE' })
    loadEvents()
  }

  const handleClear = async () => {
    if (!confirm('Clear ALL events from Screen B for this round?')) return
    await apiFetch(`/api/sim/${simId}/screen-b/events/clear`, { method: 'POST' })
    loadEvents()
  }

  const handleFreeze = async () => {
    setLoading(true)
    try { await apiFetch(`/api/sim/${simId}/screen-b/freeze`, { method: 'POST' }) }
    finally { setLoading(false) }
  }

  const handlePublish = async () => {
    setLoading(true)
    try { await apiFetch(`/api/sim/${simId}/screen-b/publish`, { method: 'POST' }) }
    finally { setLoading(false) }
  }

  const handleAddCustom = async () => {
    if (!customHeadline.trim()) return
    await apiFetch(`/api/sim/${simId}/screen-b/events`, {
      method: 'POST',
      body: JSON.stringify({ quadrant: customQuadrant, headline: customHeadline.trim(), description: customDescription.trim() }),
    })
    setCustomHeadline('')
    setCustomDescription('')
    setShowCustom(false)
    loadEvents()
  }

  const handlePromote = async (obsEvt: ObservatoryEvent) => {
    // Determine quadrant from category
    const catMap: Record<string, string> = {
      military: 'military', diplomatic: 'diplomacy', political: 'political',
      economic: 'economy_tech', covert: 'political', system: 'political',
    }
    const quadrant = catMap[obsEvt.category || ''] || 'political'
    const parts = obsEvt.summary.split(' — ', 2)
    await apiFetch(`/api/sim/${simId}/screen-b/events`, {
      method: 'POST',
      body: JSON.stringify({
        quadrant,
        headline: parts[0] || obsEvt.summary,
        description: parts[1] || '',
        source_event_id: obsEvt.id,
      }),
    })
    loadEvents()
  }

  const handleSaveEdit = async (eventId: string) => {
    await apiFetch(`/api/sim/${simId}/screen-b/events/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify({ headline: editHeadline, description: editDescription }),
    })
    setEditingId(null)
    loadEvents()
  }

  const loadObsEvents = async () => {
    const { data } = await supabase.from('observatory_events')
      .select('id,event_type,category,country_code,summary,created_at')
      .eq('sim_run_id', simId)
      .in('visibility', ['public', 'moderator'])
      .neq('event_type', 'ai_agent_log')
      .order('created_at', { ascending: false })
      .limit(100)
    setObsEvents(data || [])
  }

  const filteredObs = obsEvents.filter(e => {
    if (!obsFilter) return true
    const q = obsFilter.toLowerCase()
    return (e.summary?.toLowerCase().includes(q) || e.event_type?.toLowerCase().includes(q) || e.country_code?.toLowerCase().includes(q))
  })

  const byQuadrant = (q: string) => events.filter(e => e.quadrant === q).sort((a, b) => a.sort_order - b.sort_order)

  return (
    <div className="mt-3 space-y-2">
      {/* Controls row */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => window.open(`/screen/${simId}`, '_blank')}
          className="font-body text-caption font-medium bg-action/10 text-action px-2.5 py-1 rounded hover:bg-action/20 transition-colors"
        >
          Screen A ↗
        </button>
        <button
          onClick={() => {
            const url = `/screen/${simId}`
            const w = window.open(url, '_blank')
            // Switch to B after load
            setTimeout(() => { if (w) w.postMessage({ type: 'switch-screen', screen: 'b' }, '*') }, 1000)
          }}
          className="font-body text-caption font-medium bg-action/10 text-action px-2.5 py-1 rounded hover:bg-action/20 transition-colors"
        >
          Screen B ↗
        </button>

        <span className={`font-data text-caption px-2 py-0.5 rounded ${frozen ? 'bg-warning/15 text-warning' : 'bg-success/15 text-success'}`}>
          {frozen ? 'FROZEN' : 'LIVE'}
        </span>

        {!frozen ? (
          <button onClick={handleFreeze} disabled={loading}
            className="font-body text-caption font-medium bg-warning/10 text-warning px-2.5 py-1 rounded hover:bg-warning/20 disabled:opacity-40 transition-colors">
            Freeze
          </button>
        ) : (
          <button onClick={handlePublish} disabled={loading}
            className="font-body text-caption font-medium bg-success/10 text-success px-2.5 py-1 rounded hover:bg-success/20 disabled:opacity-40 transition-colors">
            Publish Report
          </button>
        )}

        <button onClick={handleClear}
          className="font-body text-caption font-medium bg-danger/10 text-danger px-2.5 py-1 rounded hover:bg-danger/20 transition-colors">
          Clear All
        </button>
      </div>

      {/* 2×2 quadrant editor — proportions mirror Screen B layout */}
      <div className="grid grid-cols-2 gap-3" style={{ minHeight: '320px' }}>
        {QUADRANTS.map(q => (
          <div key={q} className="bg-base border border-border rounded-md flex flex-col overflow-hidden min-h-[150px]">
            <div className={`px-3 py-1.5 border-b border-border ${QUADRANT_COLORS[q]}`}>
              <span className="font-heading text-[13px] font-bold">{QUADRANT_LABELS[q]}</span>
            </div>
            <div className="flex-1 px-3 py-1.5 overflow-y-auto">
              {byQuadrant(q).map(evt => (
                <div key={evt.id} className="py-1.5 border-b border-border/30 last:border-0 group">
                  {editingId === evt.id ? (
                    <div className="space-y-1">
                      <input value={editHeadline} onChange={e => setEditHeadline(e.target.value)}
                        className="w-full bg-card border border-border rounded px-2 py-1 font-body text-[12px] text-text-primary focus:outline-none focus:border-action" />
                      <input value={editDescription} onChange={e => setEditDescription(e.target.value)}
                        className="w-full bg-card border border-border rounded px-2 py-1 font-body text-[12px] text-text-secondary focus:outline-none focus:border-action" />
                      <div className="flex gap-2">
                        <button onClick={() => handleSaveEdit(evt.id)} className="font-body text-[10px] text-success hover:underline">Save</button>
                        <button onClick={() => setEditingId(null)} className="font-body text-[10px] text-text-secondary hover:underline">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-1">
                      <div className="flex-1 font-body text-[12px] leading-snug text-text-primary cursor-pointer hover:text-action transition-colors"
                        onClick={() => { setEditingId(evt.id); setEditHeadline(evt.headline); setEditDescription(evt.description) }}>
                        <strong className="font-semibold">{evt.headline}</strong>
                        {evt.description && <span className="text-text-secondary"> — {evt.description}</span>}
                      </div>
                      <button onClick={() => handleDelete(evt.id)}
                        className="text-text-secondary/20 hover:text-danger text-[11px] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5">
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {byQuadrant(q).length === 0 && (
                <div className="font-body text-[11px] text-text-secondary/30 italic py-2">No events yet</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add custom / Browse buttons */}
      <div className="flex gap-2">
        <button onClick={() => setShowCustom(!showCustom)}
          className="font-body text-caption font-medium bg-accent/10 text-accent px-2.5 py-1 rounded hover:bg-accent/20 transition-colors">
          + Write Custom Event
        </button>
        <button onClick={() => { setShowBrowser(!showBrowser); if (!showBrowser) loadObsEvents() }}
          className="font-body text-caption font-medium bg-text-secondary/10 text-text-secondary px-2.5 py-1 rounded hover:bg-text-secondary/20 transition-colors">
          Browse All Events
        </button>
      </div>

      {/* Custom event form */}
      {showCustom && (
        <div className="bg-base border border-border rounded p-3 space-y-2">
          <div className="flex gap-2">
            <select value={customQuadrant} onChange={e => setCustomQuadrant(e.target.value)}
              className="bg-card border border-border rounded px-2 py-1 font-body text-caption text-text-primary focus:outline-none focus:border-action">
              {QUADRANTS.map(q => <option key={q} value={q}>{QUADRANT_LABELS[q]}</option>)}
            </select>
            <input value={customHeadline} onChange={e => setCustomHeadline(e.target.value)}
              placeholder="Headline (bold)"
              className="flex-1 bg-card border border-border rounded px-2 py-1 font-body text-caption text-text-primary placeholder:text-text-secondary/40 focus:outline-none focus:border-action" />
          </div>
          <input value={customDescription} onChange={e => setCustomDescription(e.target.value)}
            placeholder="Description (one sentence)"
            className="w-full bg-card border border-border rounded px-2 py-1 font-body text-caption text-text-primary placeholder:text-text-secondary/40 focus:outline-none focus:border-action" />
          <button onClick={handleAddCustom} disabled={!customHeadline.trim()}
            className="font-body text-caption font-medium bg-accent/10 text-accent px-3 py-1 rounded hover:bg-accent/20 disabled:opacity-40 transition-colors">
            Add Event
          </button>
        </div>
      )}

      {/* Observatory event browser */}
      {showBrowser && (
        <div className="bg-base border border-border rounded p-2 max-h-[420px] overflow-y-auto space-y-1">
          <input value={obsFilter} onChange={e => setObsFilter(e.target.value)}
            placeholder="Filter by type, country, or text..."
            className="w-full bg-card border border-border rounded px-2 py-1 font-body text-caption text-text-primary placeholder:text-text-secondary/40 focus:outline-none focus:border-action mb-1" />
          {filteredObs.map(e => (
            <div key={e.id} className="flex items-center gap-2 py-0.5 hover:bg-card/50 rounded px-1 transition-colors">
              <span className="font-data text-[9px] text-text-secondary/50 w-10 flex-shrink-0">
                {new Date(e.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
              <span className="font-data text-[9px] text-text-secondary/50 w-14 truncate flex-shrink-0">{e.event_type}</span>
              <span className="font-data text-[9px] text-text-secondary/50 w-12 truncate flex-shrink-0">{e.country_code || ''}</span>
              <span className="font-body text-caption text-text-primary truncate flex-1">{e.summary}</span>
              <button onClick={() => handlePromote(e)}
                className="font-body text-[10px] text-action hover:underline flex-shrink-0">
                Add ↗
              </button>
            </div>
          ))}
          {filteredObs.length === 0 && <div className="font-body text-caption text-text-secondary/40 italic py-2 text-center">No events</div>}
        </div>
      )}
    </div>
  )
}
